import {
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    IonSegment,
    IonSegmentButton,
    IonTitle,
    IonToolbar,
    RefresherEventDetail
} from "@ionic/react";
import {
    arrowBackOutline,
    peopleOutline,
    refreshOutline,
    statsChartOutline
} from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import * as Components from "./StatsFunction";
import {
    GameDetail,
    GamePlay,
    PlayersById,
    Team,
    getGameDetail,
    getPlayers,
    getTeams
} from "../services/mexfbaApi";
import "./GamePlayers.css";

interface RouteParams {
    id: string;
}

interface PlayerStat {
    id: string;
    [key: string]: string | number;
}

interface StatColumn {
    key: string;
    label: string;
}

interface TeamStats {
    quarterbacks: PlayerStat[];
    runners: PlayerStat[];
    receivers: PlayerStat[];
    defense: PlayerStat[];
    returns: PlayerStat[];
    kickers: PlayerStat[];
}

function getNumber(value: unknown): number {
    const parsedValue = Number(value);

    return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function getPlayerName(
    playerId: string,
    players: PlayersById
): string {
    return players[playerId]?.nombre || "Jugador sin registrar";
}

function buildTeamStats(
    plays: GamePlay[],
    teamId: string
): TeamStats {
    const quarterbacks: PlayerStat[] = [];
    const runners: PlayerStat[] = [];
    const receivers: PlayerStat[] = [];
    const defense: PlayerStat[] = [];
    const returns: PlayerStat[] = [];
    const kickers: PlayerStat[] = [];

    plays.forEach(function (play) {
        const isOffense = play.ofensiva === teamId;

        /*
            Se conserva la lógica original:
            QB, RB, WR y pateadores pertenecen al equipo ofensivo.
        */
        if (isOffense) {
            Components.getQB(play, quarterbacks);
            Components.getRB(play, runners);
            Components.getWR(play, receivers);
            Components.getK(play, kickers);
        }

        /*
            La defensiva recibe estadísticas cuando el rival está
            a la ofensiva, incluyendo jugadas especiales defensivas.
        */
        const isKickoff = play.d === "0";
        const isKickReturn =
            Boolean(play.K || play.k) && Boolean(play.ret);

        if (!isOffense) {
            if (!isKickoff && !isKickReturn) {
                Components.getDEF(play, defense);
            }
        } else if (isKickoff || isKickReturn) {
            Components.getDEF(play, defense);
        }

        /*
            Los retornos pertenecen al rival del equipo ofensivo.
        */
        if (!isOffense) {
            Components.getSt(play, returns);
        }
    });
function getPlayerStatValue(
    player: PlayerStat,
    property: string
): number {
    const source = player as unknown as Record<string, unknown>;

    return getNumber(source[property]);
}

function comparePlayerStats(
    properties: string[]
): (first: PlayerStat, second: PlayerStat) => number {
    return function (first, second) {
        const firstValue = properties.reduce(function (
            total,
            property
        ) {
            return total + getPlayerStatValue(first, property);
        }, 0);

        const secondValue = properties.reduce(function (
            total,
            property
        ) {
            return total + getPlayerStatValue(second, property);
        }, 0);

        return secondValue - firstValue;
    };
}

    quarterbacks.forEach(function (player) {
        player.qbr = Components.getQBR(
            getNumber(player.att),
            getNumber(player.comp),
            getNumber(player.ydsPas),
            getNumber(player.tdsPas),
            getNumber(player.int)
        );
    });

    return {
        quarterbacks: quarterbacks
            .filter(function (player) {
                return getNumber(player.att) > 0;
            })
            .sort(comparePlayerStats(["ydsPas"])),

        runners: runners
            .filter(function (player) {
                return getNumber(player.attA) > 0;
            })
            .sort(comparePlayerStats(["ydsAca"])),

        receivers: receivers
            .filter(function (player) {
                return getNumber(player.attR) > 0;
            })
            .sort(comparePlayerStats(["ydsRec"])),

        defense: defense
            .filter(function (player) {
                return (
                    getNumber(player.tkl) +
                        getNumber(player.assTkl) +
                        getNumber(player.sack) +
                        getNumber(player.asSack) +
                        getNumber(player.int) +
                        getNumber(player.ff) +
                        getNumber(player.fr) +
                        getNumber(player.pd) +
                        getNumber(player.pb) >
                    0
                );
            })
            .sort(comparePlayerStats(["tkl", "assTkl"])),

        returns: returns
            .filter(function (player) {
                return (
                    getNumber(player.attK) +
                        getNumber(player.attP) >
                    0
                );
            })
            .sort(comparePlayerStats(["ydsK", "ydsP"])),

        kickers: kickers
            .filter(function (player) {
                return (
                    getNumber(player.attXP) +
                        getNumber(player.attFG) +
                        getNumber(player.punt) >
                    0
                );
            })
            .sort(comparePlayerStats(["compFG", "compXP"]))
    };
}

interface StatsTableProps {
    title: string;
    subtitle: string;
    columns: StatColumn[];
    players: PlayerStat[];
    playerIndex: PlayersById;
    onPlayerClick: (playerId: string) => void;
    formatValue?: (
        player: PlayerStat,
        columnKey: string
    ) => string | number;
}

const StatsTable: React.FC<StatsTableProps> = function ({
    title,
    subtitle,
    columns,
    players,
    playerIndex,
    onPlayerClick,
    formatValue
}) {
    if (players.length === 0) {
        return null;
    }

    return (
        <section className="game-players-section">
            <div className="game-players-section__heading">
                <div>
                    <span>{subtitle}</span>
                    <h2>{title}</h2>
                </div>

                <IonBadge>{players.length}</IonBadge>
            </div>

            <div className="game-players-table-wrap">
                <table className="game-players-table">
                    <thead>
                        <tr>
                            <th>JUGADOR</th>

                            {columns.map(function (column) {
                                return (
                                    <th key={column.key}>
                                        {column.label}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>

                    <tbody>
                        {players.map(function (player) {
                            return (
                                <tr key={player.id}>
                                    <td>
                                        <button
                                            type="button"
                                            onClick={function () {
                                                onPlayerClick(player.id);
                                            }}
                                        >
                                            {getPlayerName(
                                                player.id,
                                                playerIndex
                                            )}
                                        </button>
                                    </td>

                                    {columns.map(function (column) {
                                        const value = formatValue
                                            ? formatValue(
                                                  player,
                                                  column.key
                                              )
                                            : player[column.key] ?? 0;

                                        return (
                                            <td key={column.key}>
                                                {value}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

const GamePlayers: React.FC = () => {
    const { id } = useParams<RouteParams>();
    const history = useHistory();

    const [gameDetail, setGameDetail] =
        useState<GameDetail | null>(null);
    const [teams, setTeams] = useState<Team[]>([]);
    const [players, setPlayers] = useState<PlayersById>({});
    const [selectedTeamId, setSelectedTeamId] = useState("");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const loadGame = useCallback(
        async function (forceRefresh = false) {
            try {
                setError("");

                if (forceRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                const [detail, teamsData, playersData] =
                    await Promise.all([
                        getGameDetail(id, forceRefresh),
                        getTeams(forceRefresh),
                        getPlayers(forceRefresh)
                    ]);

                setGameDetail(detail);
                setTeams(teamsData);
                setPlayers(playersData);

                setSelectedTeamId(function (currentTeamId) {
                    if (
                        currentTeamId === detail.Data.id_local ||
                        currentTeamId === detail.Data.id_visita
                    ) {
                        return currentTeamId;
                    }

                    return detail.Data.id_local;
                });
            } catch (requestError) {
                console.error(
                    "No fue posible cargar estadísticas de jugadores:",
                    requestError
                );

                setError(
                    "No fue posible cargar las estadísticas de jugadores."
                );
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [id]
    );

    useEffect(
        function () {
            loadGame();
        },
        [loadGame]
    );

    const teamsById = useMemo(function () {
        return new Map(
            teams.map(function (team) {
                return [team.id, team];
            })
        );
    }, [teams]);

    const plays = useMemo(function (): GamePlay[] {
        if (!gameDetail?.Jugadas) {
            return [];
        }

        return Array.isArray(gameDetail.Jugadas)
            ? gameDetail.Jugadas
            : Object.values(gameDetail.Jugadas);
    }, [gameDetail]);

    const stats = useMemo(function (): TeamStats {
        if (!selectedTeamId) {
            return {
                quarterbacks: [],
                runners: [],
                receivers: [],
                defense: [],
                returns: [],
                kickers: []
            };
        }

        return buildTeamStats(plays, selectedTeamId);
    }, [plays, selectedTeamId]);

    async function handleRefresh(
        event: CustomEvent<RefresherEventDetail>
    ) {
        await loadGame(true);
        event.detail.complete();
    }

    if (loading) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>Jugadores</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent>
                    <IonProgressBar type="indeterminate" />
                </IonContent>
            </IonPage>
        );
    }

    if (!gameDetail || error) {
        return (
            <IonPage>
                <IonHeader>
                    <IonToolbar>
                        <IonButtons slot="start">
                            <IonButton
                                aria-label="Regresar"
                                onClick={function () {
                                    history.goBack();
                                }}
                            >
                                <IonIcon icon={arrowBackOutline} />
                            </IonButton>
                        </IonButtons>

                        <IonTitle>Jugadores</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="game-players-content">
                    <div className="game-players-empty">
                        <IonIcon icon={peopleOutline} />
                        <strong>
                            No fue posible cargar los jugadores
                        </strong>
                        <span>{error}</span>

                        <IonButton
                            onClick={function () {
                                loadGame(true);
                            }}
                        >
                            Reintentar
                        </IonButton>
                    </div>
                </IonContent>
            </IonPage>
        );
    }

    const game = gameDetail.Data;
    const localTeam = teamsById.get(game.id_local);
    const visitorTeam = teamsById.get(game.id_visita);
    const selectedTeam = teamsById.get(selectedTeamId);

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar className="game-players-toolbar">
                    <IonButtons slot="start">
                        <IonButton
                            aria-label="Regresar al partido"
                            onClick={function () {
                                history.push(`/game/${id}`);
                            }}
                        >
                            <IonIcon icon={arrowBackOutline} />
                        </IonButton>
                    </IonButtons>

                    <IonTitle>Jugadores</IonTitle>

                    <IonButtons slot="end">
                        <IonButton
                            aria-label="Actualizar estadísticas"
                            disabled={refreshing}
                            onClick={function () {
                                loadGame(true);
                            }}
                        >
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="game-players-content">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent
                        pullingText="Desliza para actualizar"
                        refreshingText="Actualizando estadísticas..."
                    />
                </IonRefresher>

                <section className="game-players-score">
                    <button
                        type="button"
                        onClick={function () {
                            history.push(`/team/${game.id_local}`);
                        }}
                    >
                        {localTeam?.logo ? (
                            <img
                                src={localTeam.logo}
                                alt={`Logo de ${game.nombre_local}`}
                            />
                        ) : (
                            <span>
                                {game.nombre_local?.charAt(0) || "L"}
                            </span>
                        )}

                        <strong>{game.nombre_local || "Local"}</strong>
                    </button>

                    <div>
                        <strong>{game.resLocal ?? "0"}</strong>
                        <span>—</span>
                        <strong>{game.resVisita ?? "0"}</strong>
                    </div>

                    <button
                        type="button"
                        onClick={function () {
                            history.push(`/team/${game.id_visita}`);
                        }}
                    >
                        {visitorTeam?.logo ? (
                            <img
                                src={visitorTeam.logo}
                                alt={`Logo de ${game.nombre_visita}`}
                            />
                        ) : (
                            <span>
                                {game.nombre_visita?.charAt(0) || "V"}
                            </span>
                        )}

                        <strong>
                            {game.nombre_visita || "Visitante"}
                        </strong>
                    </button>
                </section>

                <nav className="game-players-navigation">
                    <IonButton
                        fill="clear"
                        onClick={function () {
                            history.push(`/game/${id}`);
                        }}
                    >
                        Datos
                    </IonButton>

                    <IonButton
                        fill="clear"
                        onClick={function () {
                            history.push(`/gamePlays/${id}`);
                        }}
                    >
                        Jugadas
                    </IonButton>

                    <IonButton
                        fill="solid"
                        onClick={function () {
                            history.push(`/gamePlayers/${id}`);
                        }}
                    >
                        Jugadores
                    </IonButton>

                    <IonButton
                        fill="clear"
                        onClick={function () {
                            history.push(`/gameStats/${id}`);
                        }}
                    >
                        Stats
                    </IonButton>
                </nav>

                <section className="game-players-team-selector">
                    <span>EQUIPO</span>

                    <IonSegment
                        value={selectedTeamId}
                        onIonChange={function (event) {
                            setSelectedTeamId(
                                String(event.detail.value || "")
                            );
                        }}
                    >
                        <IonSegmentButton value={game.id_local}>
                            <span>{game.nombre_local || "Local"}</span>
                        </IonSegmentButton>

                        <IonSegmentButton value={game.id_visita}>
                            <span>
                                {game.nombre_visita || "Visitante"}
                            </span>
                        </IonSegmentButton>
                    </IonSegment>
                </section>

                <main className="game-players-list">
                    <div className="game-players-list__heading">
                        <div>
                            <span>ESTADÍSTICAS INDIVIDUALES</span>
                            <h1>
                                {selectedTeam?.nombre ||
                                    (selectedTeamId === game.id_local
                                        ? game.nombre_local
                                        : game.nombre_visita)}
                            </h1>
                        </div>

                        <IonIcon icon={statsChartOutline} />
                    </div>

                    <StatsTable
                        title="Pases"
                        subtitle="QUARTERBACKS"
                        players={stats.quarterbacks}
                        playerIndex={players}
                        onPlayerClick={function (playerId) {
                            history.push(`/player/${playerId}`);
                        }}
                        columns={[
                            { key: "att", label: "ATT" },
                            { key: "comp", label: "CMP" },
                            { key: "ydsPas", label: "YDS" },
                            { key: "tdsPas", label: "TD" },
                            { key: "int", label: "INT" },
                            { key: "qbr", label: "QBR" }
                        ]}
                    />

                    <StatsTable
                        title="Carreras"
                        subtitle="CORREDORES"
                        players={stats.runners}
                        playerIndex={players}
                        onPlayerClick={function (playerId) {
                            history.push(`/player/${playerId}`);
                        }}
                        columns={[
                            { key: "attA", label: "ATT" },
                            { key: "ydsAca", label: "YDS" },
                            { key: "tdsAca", label: "TD" },
                            { key: "fumble", label: "FUM" }
                        ]}
                    />

                    <StatsTable
                        title="Recepciones"
                        subtitle="RECEPTORES"
                        players={stats.receivers}
                        playerIndex={players}
                        onPlayerClick={function (playerId) {
                            history.push(`/player/${playerId}`);
                        }}
                        columns={[
                            { key: "attR", label: "TGT" },
                            { key: "compR", label: "REC" },
                            { key: "ydsRec", label: "YDS" },
                            { key: "tdsRec", label: "TD" },
                            { key: "fumble", label: "FUM" }
                        ]}
                    />

                    <StatsTable
                        title="Defensa"
                        subtitle="DEFENSIVA"
                        players={stats.defense}
                        playerIndex={players}
                        onPlayerClick={function (playerId) {
                            history.push(`/player/${playerId}`);
                        }}
                        columns={[
                            { key: "tkl", label: "TKL" },
                            { key: "assTkl", label: "AST" },
                            { key: "sack", label: "SCK" },
                            { key: "int", label: "INT" },
                            { key: "ff", label: "FF" },
                            { key: "fr", label: "FR" },
                            { key: "pd", label: "PD" }
                        ]}
                        formatValue={function (player, columnKey) {
                            if (columnKey === "sack") {
                                const totalSacks =
                                    getNumber(player.sack) +
                                    getNumber(player.asSack) / 2;

                                return totalSacks % 1 === 0
                                    ? totalSacks
                                    : totalSacks.toFixed(1);
                            }

                            if (columnKey === "tkl") {
                                return (
                                    getNumber(player.tkl) +
                                    getNumber(player.assTkl)
                                );
                            }

                            return player[columnKey] ?? 0;
                        }}
                    />

                    <StatsTable
                        title="Retornos"
                        subtitle="EQUIPOS ESPECIALES"
                        players={stats.returns}
                        playerIndex={players}
                        onPlayerClick={function (playerId) {
                            history.push(`/player/${playerId}`);
                        }}
                        columns={[
                            { key: "attK", label: "KR" },
                            { key: "ydsK", label: "YDS KR" },
                            { key: "tdsK", label: "TD KR" },
                            { key: "attP", label: "PR" },
                            { key: "ydsP", label: "YDS PR" },
                            { key: "tdsP", label: "TD PR" }
                        ]}
                    />

                    <StatsTable
                        title="Patadas"
                        subtitle="PATEADORES Y PUNTERS"
                        players={stats.kickers}
                        playerIndex={players}
                        onPlayerClick={function (playerId) {
                            history.push(`/player/${playerId}`);
                        }}
                        columns={[
                            { key: "attXP", label: "XP ATT" },
                            { key: "compXP", label: "XP OK" },
                            { key: "attFG", label: "FG ATT" },
                            { key: "compFG", label: "FG OK" },
                            { key: "punt", label: "PUNT" },
                            { key: "ydsFG", label: "FG LARGO" }
                        ]}
                        formatValue={function (player, columnKey) {
                            if (columnKey === "ydsFG") {
                                const yards = getNumber(player.ydsFG);

                                return yards > 0 ? yards : "-";
                            }

                            return player[columnKey] ?? 0;
                        }}
                    />

                    {stats.quarterbacks.length === 0 &&
                    stats.runners.length === 0 &&
                    stats.receivers.length === 0 &&
                    stats.defense.length === 0 &&
                    stats.returns.length === 0 &&
                    stats.kickers.length === 0 ? (
                        <div className="game-players-no-stats">
                            <IonIcon icon={peopleOutline} />
                            <strong>
                                No hay estadísticas individuales
                            </strong>
                            <span>
                                Este equipo todavía no tiene jugadas
                                registradas en el partido.
                            </span>
                        </div>
                    ) : null}
                </main>
            </IonContent>
        </IonPage>
    );
};

export default GamePlayers;
